/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import React from "react";
import MediaQuery from "react-responsive";
import { ContentModel } from "../../models/ContentModel";
import { Utils } from "../../utils";
import DetailItemImage from "./detail-item-image";

type DataProps = {
  index: number;
  color?: string;
  item: ContentModel | null;
  pictureAll?: string[];
  isLast?: boolean;
};

const DetailItem = ({
  index,
  color = "black",
  item,
  pictureAll = [],
  isLast = false,
}: DataProps): JSX.Element => {
  if (!item) {
    return <div style={{ flex: 1 }}></div>;
  }

  if (item.type === "picture") {
    const src = item.picture.localFile.childImageSharp;
    const srcMobile = item.pictureMobile
      ? item.pictureMobile.localFile.childImageSharp
      : src;
    return (
      <div style={{ flex: 1 }}>
        <MediaQuery maxWidth={767}>
          <DetailItemImage src={srcMobile} pictureAll={pictureAll} />
        </MediaQuery>
        <MediaQuery minWidth={768}>
          <DetailItemImage src={src} pictureAll={pictureAll} />
        </MediaQuery>
      </div>
    );
  }

  if (item.type === "text") {
    let title = "";
    let text = "";
    if (process.env.GATSBY_LANG === "JP") {
      title = item.context.titleJp;
      text = item.context.textJp;
    } else if (process.env.GATSBY_LANG === "EN") {
      title = item.context.titleEn;
      text = item.context.textEn;
    }

    // text = Utils.replaceLink(text);
    text = Utils.replaceLinkToDetail(text);
    text = Utils.replaceLinkToParent(text);
    text = Utils.replaceItalic(text);
    text = Utils.replaceBold(text);

    return (
      <div
        css={css`
          flex: 1;
          display: flex;
          @media (min-width: 768px) {
            flex-direction: row;
            gap: 10px;
            margin-top: ${item.useLine && index > 0 ? "52px" : "42px"};
            margin-bottom: ${isLast ? "0px" : "23px"};
          }
          @media (max-width: 767px) {
            flex-direction: column;
            margin-top: ${item.useLine && index > 0 ? "45px" : "44px"};
            margin-bottom: ${isLast ? "0px" : "23px"};
          }
          border-top: ${item.useLine ? "1px solid " + color : ""};
          padding-top: 10px;
          color: ${color};
          /* text-align: justify; */
          text-align: ${process.env.GATSBY_LANG === "JP" ? "justify" : "start"};
          word-break: break-word;

          & a {
            color: ${color};
            text-decoration-line: none;
          }
          & a:hover {
            text-decoration-line: underline;
          }
        `}
      >
        <div
          style={{
            flex: 1,
            fontWeight: "bold",
            lineHeight: "26px",
            marginBottom: title && title.length > 0 ? "24px" : "",
          }}
        >
          {title}
        </div>
        <div
          style={{ flex: 1, lineHeight: "26px" }}
          dangerouslySetInnerHTML={{ __html: text }}
        ></div>
      </div>
    );
  }

  if (item.type === "movie") {
    const src = item.movie.guid;
    const srcMobile = item.movieMobile ? item.movieMobile.guid : src;
    return (
      <div style={{ flex: 1 }}>
        <MediaQuery maxWidth={767}>
          <video
            src={srcMobile}
            style={{ width: "100%", height: "100%", display: "block" }}
            muted
            autoPlay
            loop
            playsInline
          ></video>
        </MediaQuery>
        <MediaQuery minWidth={768}>
          <video
            src={src}
            style={{ width: "100%", height: "100%", display: "block" }}
            muted
            autoPlay
            loop
            playsInline
          ></video>
        </MediaQuery>
      </div>
    );
  }

  if (item.type === "movie_url") {
    const mYoutube = item.movieUrl.match(/^.+youtube.+\/watch\?v=(.+)/);
    if (mYoutube) {
      // youtube
      const videoID = mYoutube[1];

      return (
        <div
          style={{
            flex: 1,
            position: "relative",
            paddingTop: "56.25%",
          }}
        >
          <iframe
            src={"https://www.youtube.com/embed/" + videoID}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "100%",
              height: "100%",
              border: "none",
            }}
          ></iframe>
        </div>
      );
    }
    const mVimeo = item.movieUrl.match(/^.+vimeo.+\/(.+)$/);
    if (mVimeo) {
      // vimeo
      const videoID = mVimeo[1];
      const src = `https://player.vimeo.com/video/${videoID}`;

      return (
        <React.Fragment>
          <div
            style={{
              flex: 1,
              padding: "56.25% 0 0 0",
              position: "relative",
            }}
          >
            <iframe
              src={src}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: "none",
              }}
              allow="autoplay; fullscreen; picture-in-picture"
            ></iframe>
          </div>
          {/* <script src="https://player.vimeo.com/api/player.js"></script> */}
        </React.Fragment>
      );
    }

    return (
      <div style={{ flex: 1 }}>
        {item.type}:{item.movieUrl}
      </div>
    );
  }

  return <div style={{ flex: 1 }}>{item.type}</div>;
};

export default DetailItem;
